import { Icon } from '@iconify/react';
import { Button, Selection, Spinner } from '@nextui-org/react';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ListCardContainer } from '../list-card-container';
import DataTable from '../table/data-table';
import ViewToggle from '../view-toggle';

interface DataViewProps<T extends { id: string | number }> {
  data: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableConfig: any;
  viewType: 'tile' | 'list';
  onViewChange: (view: 'tile' | 'list') => void;
  renderCard: (item: T) => React.ReactNode;
  onSelectionChange?: (keys: Selection) => void;
  selectedKeys?: Selection;
  title?: string;
  actions?: React.ReactNode;
  disableTileView?: boolean;
  isLoading?: boolean;
  // New props for infinite scroll
  infiniteScroll?: {
    hasMore: boolean;
    next: () => void;
    totalCount: number;
  };
}

function DataView<T extends { id: string | number }>({
  data,
  tableConfig,
  viewType = 'tile',
  onViewChange,
  renderCard,
  onSelectionChange,
  selectedKeys,
  title,
  actions,
  disableTileView = false,
  isLoading = false,
  infiniteScroll,
}: DataViewProps<T>) {
  const isListView = disableTileView || viewType === 'list';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const mobileMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (mobileMenuRef.current?.contains(event.target as Node)) return;
      setIsMobileMenuOpen(false);
    };

    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden min-w-0 p-2 sm:p-8">
      <div className="flex shrink-0 items-center gap-2 mb-4 mt-2 sm:mt-0">
        {title && (
          <h1 className="min-w-0 flex-1 truncate text-2xl font-bold">
            {title}
          </h1>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-4">
          {!disableTileView && (
            <ViewToggle viewType={viewType} onChange={onViewChange} />
          )}
          {actions && <div className="hidden sm:flex">{actions}</div>}
          {actions && (
            <div ref={mobileMenuRef} className="relative sm:hidden">
              <Button
                isIconOnly
                size="sm"
                variant="flat"
                aria-label="Open page menu"
                aria-expanded={isMobileMenuOpen}
                onPress={() => setIsMobileMenuOpen((open) => !open)}
              >
                <Icon icon="heroicons:ellipsis-vertical" width="20" />
              </Button>
              {isMobileMenuOpen && (
                <div className="absolute right-0 top-full z-20 mt-1 min-w-[12rem] rounded-lg border border-default-200 bg-content1 p-3 shadow-lg">
                  <div
                    className="flex flex-col gap-2 [&_.flex]:!flex-col [&_.flex]:items-stretch [&_button]:w-full"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {actions}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {data?.length === 0 && !isLoading ? (
        <div className="text-center">{`No data to display`}</div>
      ) : isListView ? (
        <div className="w-full min-w-0 pb-1 mt-2 sm:mt-0">
          <DataTable<T>
            config={tableConfig}
            data={data}
            onSelectionChange={onSelectionChange}
            selectedKeys={selectedKeys}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <div
          className="max-h-[calc(100vh-100px)] min-w-0 overflow-y-auto bg-transparent"
          id="scrollableDiv"
        >
          <InfiniteScroll
            dataLength={data.length}
            next={infiniteScroll?.next || (() => {})}
            hasMore={infiniteScroll?.hasMore || false}
            loader={
              <div className="flex flex-col justify-center py-4">
                <Spinner size="sm" />
              </div>
            }
            scrollableTarget="scrollableDiv"
          >
            <ListCardContainer isInsideDataView>
              <React.Fragment>
                {data?.map((item) => renderCard(item))}
              </React.Fragment>
            </ListCardContainer>
          </InfiniteScroll>
        </div>
      )}
    </div>
  );
}

export default DataView;
